import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, Loader2, Search } from 'lucide-react';

type FreeGiftComboRow = {
  gift_card_sku: string;
  free_gift_sku: string[];
  start_date: string; // YYYY-MM-DD
  end_date: string; // YYYY-MM-DD
};

type GiftCardProductRow = {
  product_id: number;
  sku: string;
};

type PrefetchEntry =
  | { status: 'idle'; products: GiftCardProductRow[] }
  | { status: 'loading'; products: GiftCardProductRow[] }
  | { status: 'done'; products: GiftCardProductRow[] }
  | { status: 'error'; products: GiftCardProductRow[]; error: string };

const TZ8 = 'Asia/Singapore';
const DEFAULT_START_DATE = '2025-11-01';

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function getTz8YearMonthNow(): { year: number; monthIndex: number } {
  // Get the "current" year/month in +08:00 regardless of user's local timezone.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TZ8,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = Number(parts.find((p) => p.type === 'year')?.value);
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  return { year, monthIndex: month - 1 };
}

function getDefaultRangeTz8(): { startDate: string; endDate: string } {
  const { year, monthIndex } = getTz8YearMonthNow();
  const endAnchor = new Date(Date.UTC(year, monthIndex + 1, 0));

  const startDate = DEFAULT_START_DATE;
  const endDate = `${endAnchor.getUTCFullYear()}-${pad2(endAnchor.getUTCMonth() + 1)}-${pad2(endAnchor.getUTCDate())}`;
  return { startDate, endDate };
}

function comboKey(r: FreeGiftComboRow) {
  const freeSet = (r.free_gift_sku || []).map((x) => String(x).trim()).filter(Boolean).join(',');
  return `${r.gift_card_sku}__${r.start_date}__${r.end_date}__${freeSet}`;
}

export function GiftCardEventsPage() {
  const [rows, setRows] = useState<FreeGiftComboRow[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [productsByComboKey, setProductsByComboKey] = useState<Record<string, PrefetchEntry>>({});
  const [openCombo, setOpenCombo] = useState<FreeGiftComboRow | null>(null);
  const [checkedSkus, setCheckedSkus] = useState<Set<string>>(new Set());
  const [initialCheckedSkus, setInitialCheckedSkus] = useState<Set<string>>(new Set());
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const filteredRows = useMemo(() => {
    const sortedRows = rows
      .slice()
      .sort((a, b) => {
        const byStartDate = b.start_date.localeCompare(a.start_date);
        if (byStartDate !== 0) return byStartDate;
        return a.gift_card_sku.localeCompare(b.gift_card_sku);
      });
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sortedRows;
    return sortedRows.filter((r) => {
      if ((r.gift_card_sku || '').toLowerCase().includes(q)) return true;
      if ((r.free_gift_sku || []).some((x) => String(x).toLowerCase().includes(q))) return true;
      return false;
    });
  }, [rows, searchQuery]);

  async function fetchCombos(params: { startDate: string; endDate: string }) {
    const qs = new URLSearchParams({ start_date: params.startDate, end_date: params.endDate });
    const resp = await fetch(`/v1/api/gp-reporter/free-gifts/combos/?${qs.toString()}`, { method: 'GET' });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
    }
    const data = (await resp.json()) as unknown;
    const arr = Array.isArray(data) ? data : [];
    const parsed: FreeGiftComboRow[] = arr
      .map((x) => {
        const gift_card_sku = String((x as any)?.gift_card_sku ?? '').trim();
        const free_gift_sku = Array.isArray((x as any)?.free_gift_sku) ? (x as any).free_gift_sku.map(String) : [];
        const start_date = String((x as any)?.start_date ?? '').trim();
        const end_date = String((x as any)?.end_date ?? '').trim();
        if (!gift_card_sku || !start_date || !end_date) return null;
        return { gift_card_sku, free_gift_sku, start_date, end_date };
      })
      .filter(Boolean) as FreeGiftComboRow[];
    return parsed;
  }

  async function fetchGiftCardProducts(params: { giftCardSku: string; startDate: string; endDate: string }) {
    const resp = await fetch('/v1/api/gp-reporter/free-gifts/gift-card-products/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        gift_card_sku: params.giftCardSku,
        start_date: params.startDate,
        end_date: params.endDate,
      }),
    });
    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
    }
    const data = (await resp.json()) as unknown;
    const arr = Array.isArray(data) ? data : [];
    const parsed: GiftCardProductRow[] = arr
      .map((x) => {
        const product_id = Number((x as any)?.product_id);
        const sku = String((x as any)?.sku ?? '').trim();
        if (!Number.isFinite(product_id) || !sku) return null;
        return { product_id, sku };
      })
      .filter(Boolean) as GiftCardProductRow[];
    return parsed;
  }

  async function prefetchGiftCardProductsForCombos(combos: FreeGiftComboRow[]) {
    setProductsByComboKey({});

    for (const c of combos) {
      const key = comboKey(c);
      setProductsByComboKey((prev) => ({ ...prev, [key]: { status: 'loading', products: [] } }));
      try {
        const products = await fetchGiftCardProducts({
          giftCardSku: c.gift_card_sku,
          startDate: c.start_date,
          endDate: c.end_date,
        });
        setProductsByComboKey((prev) => ({ ...prev, [key]: { status: 'done', products } }));
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to prefetch gift card products.';
        setProductsByComboKey((prev) => ({ ...prev, [key]: { status: 'error', products: [], error: msg } }));
      }
    }
  }

  async function loadDefaultRange() {
    const range = getDefaultRangeTz8();
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCombos(range);
      setRows(data);
      void prefetchGiftCardProductsForCombos(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load gift card combos.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadDefaultRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // When opening a combo, pre-check the free_gift_sku set by default.
    if (!openCombo) return;
    const initial = new Set((openCombo.free_gift_sku || []).map((x) => String(x).trim()).filter(Boolean));
    setInitialCheckedSkus(initial);
    setCheckedSkus(new Set(initial));
    setSaveError(null);
  }, [openCombo]);

  const isDirty = useMemo(() => {
    if (checkedSkus.size !== initialCheckedSkus.size) return true;
    for (const sku of checkedSkus) {
      if (!initialCheckedSkus.has(sku)) return true;
    }
    return false;
  }, [checkedSkus, initialCheckedSkus]);

  const openComboKey = openCombo ? comboKey(openCombo) : '';
  const openComboEntry = openComboKey ? productsByComboKey[openComboKey] : undefined;

  async function submitSkuChanges() {
    if (!openCombo || !isDirty || saveLoading) return;

    const add_skus = Array.from(checkedSkus).filter((x) => !initialCheckedSkus.has(x));
    const remove_skus = Array.from(initialCheckedSkus).filter((x) => !checkedSkus.has(x));

    setSaveLoading(true);
    setSaveError(null);
    try {
      const resp = await fetch('/v1/api/gp-reporter/free-gifts/update-free-gift-sku-range/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gift_card_sku: openCombo.gift_card_sku,
          start_date: openCombo.start_date,
          end_date: openCombo.end_date,
          add_skus,
          remove_skus,
        }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }

      // Refresh combos so grouping/date ranges stay consistent with DB.
      await loadDefaultRange();
      setOpenCombo(null);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save changes.');
    } finally {
      setSaveLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header with Search and Refresh */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search gift card SKU / free gift SKU..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={loadDefaultRange} disabled={loading} className="gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Refresh
        </Button>
      </div>

      {/* Showing count */}
      <div className="text-sm text-muted-foreground">
        {startDate && endDate ? (
          <>
            Showing {filteredRows.length} combos for <span className="font-medium">{startDate}</span> to{' '}
            <span className="font-medium">{endDate}</span> (TZ: +08)
          </>
        ) : (
          <>Loading date range…</>
        )}
      </div>

      {/* Table */}
      {error && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <span className="break-words">{error}</span>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">Gift Card SKU</TableHead>
              <TableHead className="w-[140px]">Start Date</TableHead>
              <TableHead className="w-[140px]">End Date</TableHead>
              <TableHead className="w-[200px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.length > 0 ? (
              filteredRows.map((r, idx) => (
                <TableRow key={`${r.gift_card_sku}-${r.start_date}-${r.end_date}-${idx}`}>
                  <TableCell className="font-mono text-sm">{r.gift_card_sku}</TableCell>
                  <TableCell className="text-sm">{r.start_date}</TableCell>
                  <TableCell className="text-sm">{r.end_date}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setOpenCombo(r)}
                      className="text-xs"
                    >
                      check gift card sku
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                  {loading ? 'Loading combos…' : 'No combos found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!openCombo} onOpenChange={(open) => !open && setOpenCombo(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Gift card products</DialogTitle>
            <DialogDescription>
              {openCombo ? (
                <>
                  Gift card SKU: <span className="font-mono">{openCombo.gift_card_sku}</span> | Range:{' '}
                  <span className="font-mono">
                    {openCombo.start_date} to {openCombo.end_date}
                  </span>
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          {openComboEntry?.status === 'loading' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading products…
            </div>
          )}

          {openComboEntry?.status === 'error' && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span className="break-words">{openComboEntry.error}</span>
            </div>
          )}

          {openComboEntry?.status === 'done' && (
            <div className="max-h-[60vh] overflow-auto rounded-md border">
              <div className="divide-y">
                {(openComboEntry.products || []).map((p) => {
                  const isChecked = checkedSkus.has(p.sku);
                  return (
                    <label key={`${p.product_id}-${p.sku}`} className="flex items-center gap-3 px-4 py-2 text-sm">
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={isChecked}
                        onChange={(e) => {
                          const next = new Set(checkedSkus);
                          if (e.currentTarget.checked) next.add(p.sku);
                          else next.delete(p.sku);
                          setCheckedSkus(next);
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono truncate">{p.sku}</div>
                        <div className="text-xs text-muted-foreground">product_id: {p.product_id}</div>
                      </div>
                    </label>
                  );
                })}

                {(openComboEntry.products || []).length === 0 && (
                  <div className="px-4 py-6 text-sm text-muted-foreground">No products found for this combo.</div>
                )}
              </div>
            </div>
          )}

          {saveError && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span className="break-words">{saveError}</span>
            </div>
          )}

          {(openComboEntry?.status === 'done' || openComboEntry?.status === 'error') && (
            <div className="flex items-center justify-end gap-2 pt-2">
              {isDirty && (
                <Button onClick={submitSkuChanges} disabled={saveLoading} className="gap-2">
                  {saveLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Confirm changes
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
